function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {

  if (!elementToFocus) return;

  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}


class SliderComponent extends HTMLElement {
  constructor() {
    super();

    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');


    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slideshow__bullet'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    /* this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
    this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
    this.autoplayButtonIsSetToPlay = true; */
    this.play();

  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }


    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);

  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slideshow__bullet');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slideshow__bullet--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slideshow__bullet--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    this.play();
  }

  focusInHandling(event) {
    this.pause();
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
      (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);


/* -------------------------------------------------------------------------- */
/*                               VARIANT PCIKER                               */
/* -------------------------------------------------------------------------- */

// Güvenlik ağı: theme.liquid içindeki inline script bir müşteri metafield'i yüzünden
// bozulursa window.variantStrings tanımsız kalabiliyor. Bu durumda `.notifyMe` gibi
// erişimler onVariantChange'i komple çökertir (medya/fiyat/URL güncellemeleri dahil).
// Boş bir nesneyle başlatıp varyant değişiminin en azından çalışmaya devam etmesini sağlıyoruz.
window.variantStrings = window.variantStrings || {};

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);
    this.updatePickupAvailability();
    this.removeErrorMessage();
    this.updateVariantStatuses();

    // Variant mevcut değilse "stokta yok" durumunu göster
    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      // Seçilen varyantın stok durumuna göre butonu hemen güncelle.
      // renderProductInfo'nun async fetch'ini beklemeden: fetch gecikse ya da
      // başarısız olsa bile buton metni ("Sepete ekle" / "Stoğa geldiğinde haber ver") doğru kalır.
      this.toggleAddButton(!this.currentVariant.available, window.variantStrings.notifyMe);
      this.renderProductInfo();
      this.updateShareUrl();
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    const mediaGalleries = document.querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`);
    mediaGalleries.forEach((mediaGallery) =>
      mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true)
    );

    const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
    if (!modalContent) return;
    const newMediaModal = modalContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
    modalContent.prepend(newMediaModal);
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateVariantStatuses() {
    const selectedOptionOneVariants = this.variantData.filter(
      (variant) => this.querySelector(':checked').value === variant.option1
    );
    const inputWrappers = [...this.querySelectorAll('.product-form__input')];
    inputWrappers.forEach((option, index) => {
      if (index === 0) return;
      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
      const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
      const availableOptionInputsValue = selectedOptionOneVariants
        .filter((variant) => variant.available && variant[`option${index}`] === previousOptionSelected)
        .map((variantOption) => variantOption[`option${index + 1}`]);
      this.setInputAvailability(optionInputs, availableOptionInputsValue);
    });
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.innerText = input.getAttribute('value');
      } else {
        input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', input.getAttribute('value'));
      }
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }  

  renderProductInfo() {
    const requestedVariantId = this.currentVariant.id;
    const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;

    fetch(
      `${this.dataset.url}?variant=${requestedVariantId}&section_id=${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section
      }`
    )
      .then((response) => response.text())
      .then((responseText) => {
        // prevent unnecessary ui changes from abandoned selections
        if (this.currentVariant.id !== requestedVariantId) return;

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(`price-${this.dataset.section}`);
        const source = html.getElementById(
          `price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const skuSource = html.getElementById(
          `Sku-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
        const inventorySource = html.getElementById(
          `Inventory-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);

        const volumePricingSource = html.getElementById(
          `Volume-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );

        const pricePerItemDestination = document.getElementById(`Price-Per-Item-${this.dataset.section}`);
        const pricePerItemSource = html.getElementById(`Price-Per-Item-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);

        const volumePricingDestination = document.getElementById(`Volume-${this.dataset.section}`);
        const qtyRules = document.getElementById(`Quantity-Rules-${this.dataset.section}`);
        const volumeNote = document.getElementById(`Volume-Note-${this.dataset.section}`);

        if (volumeNote) volumeNote.classList.remove('hidden');
        if (volumePricingDestination) volumePricingDestination.classList.remove('hidden');
        if (qtyRules) qtyRules.classList.remove('hidden');

        if (source && destination) destination.innerHTML = source.innerHTML;
        if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;
        if (skuSource && skuDestination) {
          skuDestination.innerHTML = skuSource.innerHTML;
          skuDestination.classList.toggle('hidden', skuSource.classList.contains('hidden'));
        }

        if (volumePricingSource && volumePricingDestination) {
          volumePricingDestination.innerHTML = volumePricingSource.innerHTML;
        }

        if (pricePerItemSource && pricePerItemDestination) {
          pricePerItemDestination.innerHTML = pricePerItemSource.innerHTML;
          pricePerItemDestination.classList.toggle('hidden', pricePerItemSource.classList.contains('hidden'));
        }

        const price = document.getElementById(`price-${this.dataset.section}`);

        if (price) price.classList.remove('hidden');

        if (inventoryDestination)
          inventoryDestination.classList.toggle('hidden', inventorySource.innerText === '');

        const addButtonUpdated = html.getElementById(`ProductSubmitButton-${sectionId}`);
        // Önce client-side varyant verisindeki stok durumuna güven; fetch'ten gelen HTML
        // yanlış/eksik gelse bile (data-back-in-stock atfı okunamazsa) buton doğru kalır.
        const variantSoldOut =
          !this.currentVariant.available ||
          (addButtonUpdated ? addButtonUpdated.hasAttribute('data-back-in-stock') : true);
        this.toggleAddButton(variantSoldOut, window.variantStrings.notifyMe);

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId,
            html,
            variant: this.currentVariant,
          },
        });
      });
  }

  toggleAddButton(disable = true, text = '', modifyClass = true) {
    const productForms = document.querySelectorAll(`.product-form-${this.dataset.section}`);
  
    productForms.forEach((form) => {
      const addButton = form.querySelector('[name="add"]');
      if (!addButton) return;
      const product_variant_id = form.querySelector('[name="id"]');
      const addButtonText = addButton.querySelector('.add-to-cart-btn__text');
  
      if (disable) {
        addButton.classList.remove('add-to-cart-btn');
        addButton.classList.add("add-to-cart-btn", "jojocode-bis-trigger");
        addButton.setAttribute("data-back-in-stock", true);
        product_variant_id.setAttribute("disabled", "disabled");
        if (text && addButtonText) {
          addButtonText.textContent = text;
        }
      } else {
        addButton.classList.remove("add-to-cart-btn", "jojocode-bis-trigger");
        addButton.classList.add('add-to-cart-btn');
        addButton.removeAttribute("data-back-in-stock");
        product_variant_id.removeAttribute("disabled");
        addButton.style.display = "flex";
        if (addButtonText && window.variantStrings.addToCart) {
          addButtonText.textContent = window.variantStrings.addToCart;
        }
      }
    });
  
    if (!modifyClass) return;
  }
  
  

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
    const sku = document.getElementById(`Sku-${this.dataset.section}`);
    const pricePerItem = document.getElementById(`Price-Per-Item-${this.dataset.section}`);
    const volumeNote = document.getElementById(`Volume-Note-${this.dataset.section}`);
    const volumeTable = document.getElementById(`Volume-${this.dataset.section}`);
    const qtyRules = document.getElementById(`Quantity-Rules-${this.dataset.section}`);

    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('hidden');
    if (inventory) inventory.classList.add('hidden');
    if (sku) sku.classList.add('hidden');
    if (pricePerItem) pricePerItem.classList.add('hidden');
    if (volumeNote) volumeNote.classList.add('hidden');
    if (volumeTable) volumeTable.classList.add('hidden');
    if (qtyRules) qtyRules.classList.add('hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.classList.remove('disabled');
      } else {
        input.classList.add('disabled');
      }
    });
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }
}

customElements.define('variant-radios', VariantRadios);


/* -------------------------------------------------------------------------- */
/*                          BACK IN STOCK POPUP                               */
/* -------------------------------------------------------------------------- */

class BackInStockPopup extends HTMLElement {
  constructor() {
    super();
    
    this.overlay = this.querySelector('.bis-popup-overlay');
    this.closeButton = this.querySelector('.bis-popup__close');
    this.form = this.querySelector('.bis-popup__form');
    this.variantSelect = this.querySelector('#bis-variant-select');
    this.emailInput = this.querySelector('#bis-email-input');
    this.newsletterCheckbox = this.querySelector('#bis-newsletter-checkbox');
    this.submitButton = this.querySelector('.bis-popup__button');
    this.buttonText = this.querySelector('.bis-popup__button-text');
    this.buttonLoading = this.querySelector('.bis-popup__button-loading');
    this.messageContainer = this.querySelector('.bis-popup__message');
    this.titleElement = this.querySelector('.bis-popup__title');
    
    this.productData = null;
    this.selectedVariantId = null;
    
    // Bind event listeners
    this.closeButton.addEventListener('click', this.close.bind(this));
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.close();
      }
    });
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
    
    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.overlay.classList.contains('hidden')) {
        this.close();
      }
    });
    
    // Load product data from JSON
    this.loadProductData();
  }
  
  loadProductData() {
    const dataScript = this.querySelector('#bis-product-data');
    if (dataScript) {
      try {
        this.productData = JSON.parse(dataScript.textContent);
      } catch (error) {
        console.error('Back In Stock: Failed to parse product data', error);
      }
    }
  }
  
  open(variantId) {
    if (!this.productData) {
      console.error('Back In Stock: Product data not loaded');
      return;
    }
    
    this.selectedVariantId = variantId;
    
    // Set popup title
    if (this.titleElement && this.productData.product) {
      this.titleElement.textContent = this.productData.product.title;
    }
    
    // Populate variant dropdown
    this.populateVariants();
    
    // Show overlay
    this.overlay.classList.remove('hidden');
    document.documentElement.classList.add('overflow-hidden');
    
    // Focus email input
    setTimeout(() => {
      this.emailInput.focus();
    }, 300);
  }
  
  close() {
    this.overlay.classList.add('hidden');
    document.documentElement.classList.remove('overflow-hidden');
    
    // Countdown interval'i temizle
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    
    // Countdown text'i gizle
    const countdownText = this.querySelector('.bis-popup__countdown-text');
    if (countdownText) {
      countdownText.classList.add('hidden');
    }
    
    this.resetForm();
  }
  
  populateVariants() {
    // Clear existing options except the first one
    this.variantSelect.innerHTML = '<option value="">Varyant seçin</option>';
    
    if (!this.productData || !this.productData.variants) {
      return;
    }
    
    // Filter unavailable variants and add to dropdown
    const unavailableVariants = this.productData.variants.filter(v => !v.available);
    
    unavailableVariants.forEach(variant => {
      const option = document.createElement('option');
      option.value = variant.id;
      option.textContent = variant.title;
      option.dataset.inventoryItemId = variant.inventory_item_id;
      option.dataset.productId = variant.product_id;
      
      // Pre-select if this is the variant that triggered the popup
      if (variant.id == this.selectedVariantId) {
        option.selected = true;
      }
      
      this.variantSelect.appendChild(option);
    });
  }
  
  validateForm() {
    const email = this.emailInput.value.trim();
    const variantId = this.variantSelect.value;
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      this.showMessage('Lütfen geçerli bir e-posta adresi girin.', 'error');
      return false;
    }
    
    // Variant selection validation
    if (!variantId) {
      this.showMessage('Lütfen bir varyant seçin.', 'error');
      return false;
    }
    
    return true;
  }
  
  async handleSubmit(event) {
    event.preventDefault();
    
    if (!this.validateForm()) {
      return;
    }
    
    // Get form data
    const email = this.emailInput.value.trim();
    const variantId = this.variantSelect.value;
    const newsletterOptIn = this.newsletterCheckbox.checked;
    
    // Get variant details from the selected option
    const selectedOption = this.variantSelect.options[this.variantSelect.selectedIndex];
    const productId = selectedOption.dataset.productId;
    const variantTitle = selectedOption.textContent;
    const productTitle = this.productData.product.title;
    
    // Show loading state
    this.setLoading(true);
    this.hideMessage();
    
    // Prepare webhook data
    const webhookData = {
      email: email,
      product_id: parseInt(productId),
      variant_id: parseInt(variantId),
      productName: productTitle,
      variantName: variantTitle,
      newsletter_accepting: newsletterOptIn
    };
    
    try {
      // Get webhook URL from data attribute (set in theme settings)
      const webhookUrl = this.dataset.webhookUrl;
      
      if (!webhookUrl) {
        throw new Error('Webhook URL not configured in theme settings');
      }
      
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookData)
      });
      
      const result = await response.json();
      
      if (response.ok && result.success !== false) {
        this.showMessage(
          result.message || 'Tamamdır. Ürün tekrar stoklarımıza geldiğinde sana haber vereceğiz.',
          'success'
        );
        
        // Countdown text'i başlat
        const countdownText = this.querySelector('.bis-popup__countdown-text');
        
        if (countdownText) {
          countdownText.classList.remove('hidden');
          
          const duration = 5000; // 5 saniye
          const interval = 1000; // 1 saniye
          let remainingSeconds = Math.ceil(duration / 1000);
          
          // Başlangıç text
          countdownText.textContent = `Bu pencere ${remainingSeconds} saniye sonra kapanacak`;
          
          const countdownInterval = setInterval(() => {
            remainingSeconds--;
            
            if (remainingSeconds > 0) {
              countdownText.textContent = `Bu pencere ${remainingSeconds} saniye sonra kapanacak`;
            } else {
              countdownText.textContent = 'Pencere kapatılıyor...';
              clearInterval(countdownInterval);
            }
          }, interval);
          
          // Store interval ID to clear on close
          this.countdownInterval = countdownInterval;
        }
        
        // Close popup after 5 seconds
        setTimeout(() => {
          if (countdownText) countdownText.classList.add('hidden');
          this.close();
        }, 5000);
      } else {
        // Handle error response
        this.showMessage(
          result.message || 'Bir hata oluştu. Lütfen daha sonra tekrar deneyin.',
          'error'


        );
      }
    } catch (error) {
      console.error('Back In Stock: Webhook request failed', error);
      this.showMessage(
        'Bağlantı hatası. Lütfen internet bağlantınızı kontrol edin ve tekrar deneyin.',
        'error'
      );
    } finally {
      this.setLoading(false);
    }
  }
  
  setLoading(isLoading) {
    this.submitButton.disabled = isLoading;
    
    if (isLoading) {
      this.buttonText.classList.add('hidden');
      this.buttonLoading.classList.remove('hidden');
    } else {
      this.buttonText.classList.remove('hidden');
      this.buttonLoading.classList.add('hidden');
    }
  }
  
  showMessage(message, type = 'success') {
    this.messageContainer.textContent = message;
    this.messageContainer.className = `bis-popup__message ${type}`;
    this.messageContainer.classList.remove('hidden');
  }
  
  hideMessage() {
    this.messageContainer.classList.add('hidden');
  }
  
  resetForm() {
    this.form.reset();
    this.hideMessage();
    this.variantSelect.innerHTML = '<option value="">Varyant seçin</option>';
  }
}

customElements.define('back-in-stock-popup', BackInStockPopup);

// Global event delegation for Back In Stock trigger buttons
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('.jojocode-bis-trigger');
  
  if (trigger && trigger.hasAttribute('data-back-in-stock')) {
    event.preventDefault();
    
    // Find the product form to get variant ID
    const productForm = trigger.closest('form[data-type="add-to-cart-form"]');
    if (!productForm) {
      console.error('Back In Stock: Product form not found');
      return;
    }
    
    // Get variant ID from the hidden input
    const variantInput = productForm.querySelector('input[name="id"]');
    if (!variantInput) {
      console.error('Back In Stock: Variant input not found');
      return;
    }
    
    const variantId = variantInput.value;
    
    // Find and open the popup
    const popup = document.querySelector('back-in-stock-popup');
    if (popup) {
      popup.open(variantId);
    } else {
      console.error('Back In Stock: Popup element not found');
    }
  }
});



/* -------------------------------------------------------------------------- */
/*                              MOBILE NAVIGATION                             */
/* -------------------------------------------------------------------------- */

class MobileNavigation extends HTMLElement {
  constructor() {
    super();

    this.hamburgerButton = this.querySelector('.mobile-header__hamburger');
    this.closeButton = this.querySelector('.mobile-navigation__close');
    this.backButton = this.querySelector('.mobile-navigation__back');
    this.dropdownParents = this.querySelectorAll('.mobile-navigation__links > ul > li.has-dropdown .mobile-menu-link-icon > svg');
    this.hasNoDropdownParents = this.querySelectorAll('.mobile-navigation__links > ul > li.mobile-menu-link--single > .mobile-menu-link-icon > svg');
    this.submenusList = this.querySelectorAll(".mobile-navigation__submenus")
    this.deepMenus = this.querySelectorAll(".mobile-navigation__submenus .has-dropdown > a");

    this.hamburgerButton.addEventListener('click', this.openMobileMenu.bind(this));
    this.closeButton.addEventListener('click', this.closeMobileMenu.bind(this));
    this.backButton.addEventListener('click', this.closeSubMenu.bind(this));


    this.dropdownParents.forEach((e) => {
      e.addEventListener('click', this.openSubMenu.bind(this))
    });

    this.hasNoDropdownParents.forEach((e) => {
      e.addEventListener('click', this.openSubMenuNoDropdown.bind(this))
    });

    this.deepMenus.forEach((e) => {
      e.addEventListener('click', this.openDeepMenu.bind(this))
    });
  }

  openMobileMenu() {
    this.classList.add('opened');
    document.body.classList.add('no-scroll');
  }

  closeMobileMenu() {
    this.classList.remove('opened');
    document.body.classList.remove('no-scroll');
  }

  openSubMenuNoDropdown(e) {
    if (e.target.closest(".mobile-menu-link--single")) {
      const link = e.target.closest('.mobile-menu-link--single').querySelector('.mobile-menu-link-title a');
      if (link) {
          window.location.href = link.getAttribute('href');
      }
    }
  };

  openSubMenu(e) {
    if (e.target.classList.contains('mobile-menu-link__pages') == false) {
      e.preventDefault();
      e.target.closest(".has-dropdown").querySelector(".mobile-navigation__submenus").classList.add('opened');
      e.target.closest(".mobile-navigation").classList.add('opened');
      document.querySelector('.mobile-navigation__body').classList.add('menu-open');
    }
  }

  closeSubMenu(e) {
    e.preventDefault();
    e.target.closest(".mobile-navigation").classList.remove('opened');
    document.querySelector('.mobile-navigation__body').classList.remove('menu-open');
    this.submenusList.forEach((e) => {
      e.classList.remove('opened');
    });
  }

  openDeepMenu(e) {
    e.preventDefault();
    const parentElement = e.target.closest(".mobile-navigation__submenu");
    if (!parentElement.classList.contains('opened')) {
      parentElement.classList.add('opened');
    } else {
      parentElement.classList.remove('opened');
    }
  }
}

customElements.define('mobile-navigation', MobileNavigation);

/* -------------------------------------------------------------------------- */
/*                                 MENU DRAVER                                */
/* -------------------------------------------------------------------------- */

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll('button:not(.localization-selector)').forEach((button) =>
      button.addEventListener('click', this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');


    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    document.documentElement.classList.add('overflow-hidden');
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;
    document.documentElement.classList.remove('overflow-hidden');
    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    console.log(parentMenuElement)
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);


/* Footer Navigation Dropdown */

let footerNagitionTitle = document.querySelectorAll('.navigation-dropdown');

footerNagitionTitle.forEach(function (e) {
  e.addEventListener('click', function () {
    e.classList.toggle('active');
    e.nextElementSibling.classList.toggle('show')
  })
})


/* -------------------------------------------------------------------------- */
/*                              STICKY NAVIGATION                             */
/* -------------------------------------------------------------------------- */
const header = document.querySelector('.header');
document.addEventListener('scroll', function () {
  if (window.scrollY > 0) {
    header.classList.add('header--scrolled');
  } else {
    header.classList.remove('header--scrolled');
  }
});

if (header && window.scrollY > 0) {
  header.classList.add('header--scrolled');
}




/* -------------------------------------------------------------------------- */
/*                            HEADER NAVIGATION TAB                           */
/* -------------------------------------------------------------------------- */

function getTotalHeaderHeight() {
  const elements = document.querySelectorAll('.header, .announcement-bar');
  let totalHeight = 0;

  elements.forEach(el => {
    const style = getComputedStyle(el);
    const height = el.offsetHeight;

    if (height > 0 && style.display !== 'none' && style.visibility !== 'hidden') {
      totalHeight += height;
    }
  });

  document.documentElement.style.setProperty('--header-height', `${totalHeight}px`);
}
function throttle(func, limit) {
  let inThrottle;
  return function () {
    if (!inThrottle) {
      func();
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

const throttledUpdate = throttle(getTotalHeaderHeight, 200);

// Eventler
document.addEventListener('DOMContentLoaded', getTotalHeaderHeight);
window.addEventListener('load', getTotalHeaderHeight);
window.addEventListener('resize', throttledUpdate);
window.addEventListener('scroll', throttledUpdate);

document.querySelectorAll('.header__dropdown-submenus li').forEach(function (e) {
  e.addEventListener("mouseover", function () {
    document.querySelectorAll('.header__dropdown-submenus li').forEach(function (e) {
      e.classList.remove('active');
    });

    document.querySelectorAll('.header__dropdown-group').forEach(function (e) {
      e.classList.remove('active');
    });

    document.querySelector('#tab-block-' + e.dataset.tabId).classList.add('active')
    e.classList.add('active');
  });
});




/* -------------------------------------------------------------------------- */
/*                                 CART DRAWER                                */
/* -------------------------------------------------------------------------- */

document.addEventListener('liquid-ajax-cart:request-end', event => {
  const { requestState, cart } = event.detail;

  if (!requestState.responseData?.ok) {
    if (document.querySelector('.toast')) {
      document.querySelector('.toast').remove();
    }

    const toastEl = document.createElement('div');
    toastEl.classList.add('toast');
    if (requestState?.responseData?.body?.description)
      toastEl.innerText = requestState?.responseData?.body?.description;
    else if (requestState?.responseData?.body?.error)
      toastEl.innerText = requestState?.responseData?.body?.error;
    document.querySelector("body").appendChild(toastEl);

    setTimeout(() => {
      toastEl.remove();
    }, 4000);
    return;
  }

  // If the "add to cart" request is successful
  if (requestState.requestType === 'add' && requestState.responseData?.ok) {

    // Add the CSS class to the "body" tag.
    if (!document.body.classList.contains('template-cart')) {
      document.body.classList.add('js-show-ajax-cart');
    }
  }

  if (window.shippingBar)
    shippingBarInit(cart);
  if (window.shippingBarAmountBasedDiscount)
    shippingBarAmountBasedDiscountInit(cart);
});


var tmpScrollTop;
document.body.addEventListener('click', event => {
  if (event.target === document.querySelector('.cart-drawer') || event.target.classList.contains("js-drawer-close")) {
    // Remove the CSS class from the "body" tag
    document.body.classList.remove('js-show-ajax-cart');
    /*document.body.style.top = 'auto';
    window.scrollTo(0, tmpScrollTop);*/
  }
});

document.querySelectorAll(".js-ajax-cart-open").forEach(function (e) {
  e.addEventListener('click', function () {
    if (!document.body.classList.contains('template-cart')) {

      document.body.classList.add('js-show-ajax-cart');
      /*tmpScrollTop = window.scrollY;;
      document.body.style.top = `-${tmpScrollTop}px`;*/
    }
  });
});


function shippingBarInit(cart) {
  const root = document.querySelector('[data-progress="shipping"]');
  if (!root) return;
  if (cart) {
    const getTotalPrice = cart.original_total_price != cart.items_subtotal_price ? cart.items_subtotal_price : cart.original_total_price;
    const totalPrice = getTotalPrice / 100;
    var stageCount = window.shippingBar.free_products.length;
    const freeProducts = window.shippingBar.free_products;
    var activeStage = 0;
    var progress = 5;
    var coefficient = 1;

    if (window.shippingBar.shippingThreshold && totalPrice >= window.shippingBar.shippingThreshold) {
      activeStage++;
      stageCount++;
      coefficient = 2;
      progress = (100 / stageCount);
    }



    freeProducts.forEach(function (e, i) {
      if (e.limit <= totalPrice) {
        progress = (100 / stageCount) * (i + coefficient);
        activeStage++;
      }
    });


    root.querySelectorAll(".cart-progress").forEach(function (e) {
      e.dataset.activeStage = activeStage;
    });

    root.querySelectorAll(".js-go-cart-counter").forEach(function(e) {
      e.innerHTML = cart.item_count;
    })
  }

  root.querySelectorAll('.cart-progress__bar-inner').forEach(function (e) {
    e.style.width = progress + '%';
  });

}

function shippingBarAmountBasedDiscountInit(cart) {
  const root = document.querySelector('[data-progress="abd"]');
  if (!root) return;
  if (!cart || !window.shippingBarAmountBasedDiscount) return;

  const abd = window.shippingBarAmountBasedDiscount;
  const tiers = (abd.tiers || []).map(t => ({
    ...t,
    limit: Number(t.limit || 0),
    rate: Number(t.rate || 0),
  }));

  const items = Array.isArray(cart.items) ? cart.items : [];

  // --- helpers ---
  const norm = (s) => String(s || "").trim().toLowerCase();

  const getExcludedVendorsSet = () => {
    const v =
      abd.excludedVendors ??
      abd.excluded_vendors ??
      abd.excludedVendor ??
      abd.excluded_vendor ??
      [];
    const arr = Array.isArray(v) ? v : String(v).split(","); // "A,B" destekle
    return new Set(arr.map(norm).filter(Boolean));
  };

  // Discount aktifse: kampanya key’ini bul (tier rate’e yakın olan yüzde indirim)
  const findCampaignKey = () => {
    const tierRates = tiers.map(t => Number(t.rate || 0)).filter(r => r > 0);
    if (!tierRates.length) return null;

    let best = null; // { key, allocated, diff }

    for (const it of items) {
      const allocs = Array.isArray(it.line_level_discount_allocations)
        ? it.line_level_discount_allocations
        : [];

      for (const a of allocs) {
        const app = a?.discount_application;
        if (!app || app.value_type !== "percentage") continue;

        const value = parseFloat(String(app.value).replace(",", "."));
        if (!Number.isFinite(value)) continue;

        // 40.01 gibi değerler için tolerans
        let closestDiff = Infinity;
        for (const r of tierRates) {
          const d = Math.abs(value - r);
          if (d < closestDiff) closestDiff = d;
        }
        if (closestDiff > 0.6) continue; // tolerans

        const allocated = Number(a.amount || app.total_allocated_amount || 0) || 0;
        const key = app.key || app.title || null;
        if (!key) continue;

        if (
          !best ||
          allocated > best.allocated ||
          (allocated === best.allocated && closestDiff < best.diff)
        ) {
          best = { key, allocated, diff: closestDiff };
        }
      }
    }

    return best ? best.key : null;
  };

  const excludedVendors = getExcludedVendorsSet();
  const campaignKey = findCampaignKey();

  // --- ELIGIBLE TOTAL (CENTS) ---
  let eligibleTotalCents = 0;

  for (const it of items) {
    const vendor = norm(it.vendor);
    if (excludedVendors.has(vendor)) continue;

    // Discount aktifse: sadece kampanya allocation'ı olan satırlar eligible
    if (campaignKey) {
      const allocs = Array.isArray(it.line_level_discount_allocations)
        ? it.line_level_discount_allocations
        : [];

      const hasCampaign = allocs.some((a) => {
        const app = a?.discount_application;
        const key = app?.key || app?.title;
        return key && key === campaignKey;
      });

      if (!hasCampaign) continue;
    }

    eligibleTotalCents += Number(it.original_line_price || 0) || 0;
  }

  const effectiveTotal = eligibleTotalCents / 100;

  // --- ACTIVE STAGE ---
  let activeStage = 0;
  for (const t of tiers) {
    if (t.limit > 0 && effectiveTotal >= t.limit) activeStage++;
  }

  // --- PROGRESS (LINEAR) ---
  const maxLimit = Math.max(...tiers.map(t => t.limit || 0), 0);
  let progress = 5;
  if (maxLimit > 0) {
    progress = (effectiveTotal / maxLimit) * 100;
    if (effectiveTotal <= 0) progress = 5;
    if (progress < 5) progress = 5;
    if (progress > 100) progress = 100;
  }

  // --- UI update ---
  root.querySelectorAll(".cart-progress").forEach((el) => {
    el.dataset.activeStage = activeStage;
  });

  root.querySelectorAll(".cart-progress__bar-inner").forEach((el) => {
    el.style.width = progress + "%";
  });

  // --- MARQUEE ---
  const marquee = root.querySelector(".amount-based-discount-marquee");
  const marqueeText = root.querySelector(".amount-based-discount-marquee__text");

  if (marquee && marqueeText) {
    const firstUnmet = tiers.find(t => t.limit > 0 && effectiveTotal < t.limit);

    if (firstUnmet) {
      const remaining = Math.max(0, firstUnmet.limit - effectiveTotal).toLocaleString("tr-TR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      const rawTitle = firstUnmet.title || "";
      const withSpaces = rawTitle.replace(/<br\s*\/?>/gi, " ");
      const tmp = document.createElement("div");
      tmp.innerHTML = withSpaces;
      const campaignName = (tmp.textContent || "").trim();

      marqueeText.innerHTML =
        `"${campaignName}" kampanyasından yararlanmanız için sepetinize indirim uygulanabilecek ürünler ile ${remaining}₺ daha eklemeniz gerekmektedir.`;

      marquee.style.display = "block";
    } else {
      marquee.style.display = "none";
    }
  }
}




if (window.liquidAjaxCart?.init) {
  // if Liquid Ajax Cart is already initialized
  if (typeof shippingBarAmountBasedDiscountInit === 'function') {
      shippingBarAmountBasedDiscountInit(window.liquidAjaxCart.cart);
  }
} else {
  // wait for Liquid Ajax Cart to be initialized
  document.addEventListener("liquid-ajax-cart:init", function (event) {
      if (typeof shippingBarAmountBasedDiscountInit === 'function') {
        shippingBarAmountBasedDiscountInit(window.liquidAjaxCart.cart);
      }
  });
}


/* -------------------------------------------------------------------------- */
/*                                SMOOTH SCROLL                               */
/* -------------------------------------------------------------------------- */

class SmoothScroll extends HTMLElement {
  constructor() {
    super();

    this.isDown = false;
    this.startX;
    this.scrollLeftTemp;
    this.velX = 0;
    this.momentumID;

    this.addEventListener('mousedown', this.mouseDown.bind(this));
    this.addEventListener('mouseleave', this.mouseLeave.bind(this));
    this.addEventListener('mouseup', this.mouseUp.bind(this));
    this.addEventListener('mousemove', this.mouseMove.bind(this));
    this.addEventListener('wheel', this.wheel.bind(this));
  }

  mouseDown(e) {
    console.log(1);
    this.isDown = true;
    this.classList.add('active');
    this.startX = e.pageX - this.offsetLeft;
    this.scrollLeftTemp = this.scrollLeft;
    this.cancelMomentumTracking();
  }

  mouseLeave() {
    this.isDown = false;
    this.classList.remove('active');
    console.log(2);
  }

  mouseUp() {
    this.isDown = false;
    this.classList.remove('active');
    this.beginMomentumTracking();
    console.log(3);
  }

  mouseMove(e) {
    if (!this.isDown) return;
    e.preventDefault();
    const x = e.pageX - this.offsetLeft;
    const walk = (x - this.startX) * 3; //scroll-fast
    var prevScrollLeft = this.scrollLeft;
    this.scrollLeft = this.scrollLeftTemp - walk;
    this.velX = this.scrollLeft - prevScrollLeft;
    console.log(4);
  }

  wheel(e) {
    this.cancelMomentumTracking.bind(this);
    console.log(5);
  }

  beginMomentumTracking() {
    this.cancelMomentumTracking();
    this.momentumID = window.requestAnimationFrame(this.momentumLoop.bind(this));
  }
  cancelMomentumTracking() {
    window.cancelAnimationFrame(this.momentumID);
  }
  momentumLoop() {
    this.scrollLeft += this.velX;
    this.velX *= 0.95;
    if (Math.abs(this.velX) > 0.5) {
      this.momentumID = window.requestAnimationFrame(this.momentumLoop.bind(this));
    }
  }
}

customElements.define('smooth-scroll', SmoothScroll);


class CardQuickAdd extends HTMLElement {
  constructor() {
    super();

    this.querySelectorAll("button").forEach((el) => {
      el.addEventListener('click', this.addToCart.bind(this));
    })
  }

  addToCart(e) {
    const btn = e.target;

    // HTML tarafında disable ettiysek, hiçbir işlem yapma
    if (btn.disabled) return;

    let body;
    if (btn.dataset.giftproduct == "true") {
      body = {
        items: [
          {
            id: btn.dataset.variantid,
            quantity: 1,
            properties: {
              isSelected: 'true'
            }
          }
        ]
      }
    } else {
      body = {
        items: [
          {
            id: btn.dataset.variantid,
            quantity: 1
          }
        ]
      }
    }

    const options = {
      lastCallback: function (requestState) {
        document.body.classList.add('js-show-ajax-cart');
      }
    }

    if (typeof window.gift === "undefined") {
      liquidAjaxCart.add(body);
    }else{
      liquidAjaxCart.add(body,
        {
          lastCallback: function(requestState) {
            for(var i=0; i < requestState.responseData.body.items.length; i++){
              for(var y=0; y < window.gift.product.length; y++) {
                if (requestState.responseData.body.items[i].variant_id == window.gift.product[y].variantID) {
                    window.gift.tempState = true;
                }
              }
            }
          }
        }
      );
    }
  }
}

customElements.define('card-quick-add', CardQuickAdd);


/* Interest checkbox */
class InterestCheckbox extends HTMLElement {
  constructor() {
    super();

    this.querySelectorAll("input[type='checkbox']").forEach((el) => el.addEventListener('input', this.toggleCheckbox.bind(this)))
  }

  toggleCheckbox() {
    if (this.querySelectorAll("input[type='checkbox']:checked").length == 2) {
      this.querySelector("input[type='hidden']").setAttribute('name', 'customer[note][Interest]');
      this.querySelectorAll("input[type='checkbox']").forEach((el) => {
        el.setAttribute('name', '');
      })
    } else {
      this.querySelector("input[type='hidden']").setAttribute('name', '');
      this.querySelectorAll("input[type='checkbox']").forEach((el) => {
        el.setAttribute('name', 'customer[note][Interest]');
      })
    }
  }
}

customElements.define("interest-checkbox", InterestCheckbox);

/* -------------------------------------------------------------------------- */
/*                               MARQUEE ELEMENT                              */
/* -------------------------------------------------------------------------- */

class MarqueeElement extends HTMLElement {
  constructor() {
    super();

    this.tmpDiv = document.createElement('div');
    this.tmpDiv.innerHTML = this.innerHTML;
    /*for (let i = 0; i < 4; i++) {
      this.tmpDiv.innerHTML += this.tmpDiv.innerHTML;
    }*/
    this.innerHTML = "";
    this.appendChild(this.tmpDiv);
  }

}

customElements.define('marquee-element', MarqueeElement);


/* -------------------------------------------------------------------------- */
/*                                PAGE ACORDION                               */
/* -------------------------------------------------------------------------- */


let pageAcordion = document.querySelectorAll('.page-acordion__header');

pageAcordion.forEach(function (e) {
  e.addEventListener('click', function () {
    if (!e.parentNode.classList.contains('show')) {
      e.parentNode.classList.add('show');
    } else {
      e.parentNode.classList.remove('show');
    }
  })
})


/* -------------------------------------------------------------------------- */
/*                                 ACORDION                                   */
/* -------------------------------------------------------------------------- */

class Acordion extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.clickAcordion.bind(this));
  }

  clickAcordion(el) {
    if (el.target.classList.contains('acordion__header')) {
      this.classList.toggle('show');
    }
  }
}

customElements.define('acordion-item', Acordion);


/* Meganenü scroll disabled */

/* const headerNavItems = document.querySelectorAll('.header__nav > ul > li');

headerNavItems.forEach(item => {
  item.addEventListener('mouseenter', () => {
    document.documentElement.classList.add('overflow-hidden');
  });

  item.addEventListener('mouseleave', () => {
    document.documentElement.classList.remove('overflow-hidden');
  });
});
 */

/* -------------------------------------------------------------------------- */
/*                   Slideshow full screen height calculator                  */
/* -------------------------------------------------------------------------- */
function slideshowHeightCalculator() {
  const announcementBar = document.querySelector(".template-index .announcement-bar");
  const fullScreenSlideshow = document.querySelector(".template-index .slideshow--full_screen");
  if (announcementBar && fullScreenSlideshow) {
    const announcementBarHeight = announcementBar.offsetHeight;
    if (window.innerHeight > 600)
      fullScreenSlideshow.style.height = `${window.innerHeight - announcementBarHeight}px`;
  }
};

window.addEventListener('resize', slideshowHeightCalculator);

slideshowHeightCalculator();


/* Filter Scrroll */

(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    define(['exports'], factory);
  } else if (typeof exports !== 'undefined') {
    factory(exports);
  } else {
    factory((root.dragscroll = {}));
  }
}(this, function (exports) {
  var _window = window;
  var _document = document;
  var mousemove = 'mousemove';
  var mouseup = 'mouseup';
  var mousedown = 'mousedown';
  var EventListener = 'EventListener';
  var addEventListener = 'add' + EventListener;
  var removeEventListener = 'remove' + EventListener;
  var newScrollX, newScrollY;

  var dragged = [];
  var reset = function (i, el) {
    for (i = 0; i < dragged.length;) {
      el = dragged[i++];
      el = el.container || el;
      el[removeEventListener](mousedown, el.md, 0);
      _window[removeEventListener](mouseup, el.mu, 0);
      _window[removeEventListener](mousemove, el.mm, 0);
    }

    // cloning into array since HTMLCollection is updated dynamically
    dragged = [].slice.call(_document.getElementsByClassName('dragscroll'));
    for (i = 0; i < dragged.length;) {
      (function (el, lastClientX, lastClientY, pushed, scroller, cont) {
        (cont = el.container || el)[addEventListener](
          mousedown,
          cont.md = function (e) {
            if (!el.hasAttribute('nochilddrag') ||
              _document.elementFromPoint(
                e.pageX, e.pageY
              ) == cont
            ) {
              pushed = 1;
              lastClientX = e.clientX;
              lastClientY = e.clientY;

              e.preventDefault();
            }
          }, 0
        );

        _window[addEventListener](
          mouseup, cont.mu = function () { pushed = 0; }, 0
        );

        _window[addEventListener](
          mousemove,
          cont.mm = function (e) {
            if (pushed) {
              (scroller = el.scroller || el).scrollLeft -=
                newScrollX = (- lastClientX + (lastClientX = e.clientX));
              scroller.scrollTop -=
                newScrollY = (- lastClientY + (lastClientY = e.clientY));
              if (el == _document.body) {
                (scroller = _document.documentElement).scrollLeft -= newScrollX;
                scroller.scrollTop -= newScrollY;
              }
            }
          }, 0
        );
      })(dragged[i++]);
    }
  }


  if (_document.readyState == 'complete') {
    reset();
  } else {
    _window[addEventListener]('load', reset, 0);
  }

  exports.reset = reset;
}));

/* -------------------------------------------------------------------------- */
/*                                 GIFT PRODUCT MODAL                         */
/* -------------------------------------------------------------------------- */

class GIFTPRODUCTMODAL extends HTMLElement {
  constructor() {
    super();
    this.querySelector('.product-card__add_button .gift-box-add-to-basket-button').addEventListener('click', this.clickAddToBasket.bind(this));
    this.querySelector('.variant-selector-close').addEventListener('click', this.removeXButton.bind(this));
  }

  removeXButton(el){
    el.target.closest(".product-card").classList.remove('variantSelector');
  }

  clickAddToBasket(el) {
    el.target.closest(".cart-drawer__suggested").querySelectorAll(".variantSelector").forEach(e => e.classList.remove('variantSelector'));
    el.target.closest(".product-card").classList.add('variantSelector');
  }
}

customElements.define('gift-product-modal', GIFTPRODUCTMODAL);

/* -------------------------------------------------------------------------- */
/*                                 COMBO PRODUCT                              */
/* -------------------------------------------------------------------------- */

class ComboProduct extends HTMLElement {
  constructor() {
    super();
    this.comboId = `combo-${Date.now()}`;
  }

  connectedCallback() {
    this.buttons = document.querySelectorAll(".add-combo-button");
    this.productItems = this.querySelectorAll(".combo-product-item");

    if (this.buttons.length) {
      this.buttons.forEach((button) => {
        button.addEventListener("click", () => this.addToCart(button));
      });
    }

    // Her selector'a change listener ekle
    this.productItems.forEach((product) => {
      const select = product.querySelector(".variant-selector");
      if (select) {
        select.addEventListener("change", () => this.checkAllVariantsSelected());
      }
    });

    // Sayfa yüklenince de kontrol
    this.checkAllVariantsSelected();
  }

  checkAllVariantsSelected() {
    let allSelected = true;

    this.productItems.forEach((product) => {
      const select = product.querySelector(".variant-selector");
      if (!select || !select.value) {
        allSelected = false;
      }
    });

    if (this.buttons.length) {
      this.buttons.forEach((button) => {
        const buttonText = button.querySelector(".btn-text");
        button.classList.toggle("disabled", !allSelected);

        if (allSelected) {
          buttonText.textContent = window.variantStrings.addToCart;
          button.removeAttribute("disabled");
        } else {
          buttonText.textContent = window.variantStrings.selectVariants;
          button.setAttribute("disabled", "disabled");
        }
      });
    }
  }

  async addToCart(clickedButton) {
    const itemsToAdd = [];
    const comboHandle = this.dataset.comboParentHandle;
    const comboTitle = this.dataset.comboParentTitle;

    this.productItems.forEach((product) => {
      const select = product.querySelector(".variant-selector");
      const variantId = select?.value;
      const quantity = parseInt(select?.dataset.quantity || "1", 10);

      if (variantId) {
        itemsToAdd.push({
          id: variantId,
          quantity: quantity,
          properties: {
            _combo_bundle_id: this.comboId,
            _combo_parent_handle: comboHandle,
            Not: `Bu ürün ${comboTitle} ürünü içerisinde kombinlenmiştir.`
          },
        });
      }
    });

    if (itemsToAdd.length > 0 && clickedButton) {
      const btnText = clickedButton.querySelector(".btn-text");
      const spinner = clickedButton.querySelector(".icon-loading");

      if (btnText && spinner) {
        btnText.style.display = "none";
        spinner.style.display = "inline-block";
      }

      try {
        const options = {
          firstCallback: (requestState) => {
            // Ekstra işlem yapılabilir
          },
          lastCallback: (requestState) => {
            if (btnText && spinner) {
              btnText.style.display = "inline-block";
              spinner.style.display = "none";
            }
          },
        };

        liquidAjaxCart.add({ items: itemsToAdd }, options);
      } catch (error) {
        console.error("❌ Sepete eklenirken hata oluştu:", error);
        if (btnText && spinner) {
          btnText.textContent = "Hata oluştu";
          btnText.style.display = "inline-block";
          spinner.style.display = "none";
        }
      }
    }
  }
}

customElements.define("combo-product", ComboProduct);
class ComboVariantRadios extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.fieldset = this.querySelector('fieldset');
    this.variantInputs = this.querySelectorAll('input[type="radio"]');

    this.variantInputs.forEach((input) => {
      input.addEventListener('change', () => this.handleVariantChange());
    });
  }

  handleVariantChange() {
    const selected = this.querySelector('input[type="radio"]:checked');
    const comboProductItem = this.closest('.combo-product-item');
    if (!selected || !comboProductItem) return;
  
    // Tüm label'lardan active class'ı kaldır
    this.querySelectorAll('label').forEach((label) => {
      label.classList.remove('active');
    });
  
    // Sadece seçilen input'un label'ına active class ekle
    const activeLabel = this.querySelector(`label[for="${selected.id}"]`);
    if (activeLabel) {
      activeLabel.classList.add('active');
    }
  
    // Sahte select üretme işlemi
    let hiddenSelect = comboProductItem.querySelector('.variant-selector');
    if (!hiddenSelect) {
      hiddenSelect = document.createElement('select');
      hiddenSelect.classList.add('variant-selector');
      hiddenSelect.style.display = 'none';
      comboProductItem.appendChild(hiddenSelect);
    }
  
    hiddenSelect.innerHTML = '';
    const option = document.createElement('option');
    option.value = selected.value;
    option.selected = true;
    hiddenSelect.appendChild(option);
    hiddenSelect.setAttribute('data-quantity', selected.dataset.quantity || "1");
  
    const comboProduct = this.closest('combo-product');
    if (comboProduct) {
      comboProduct.checkAllVariantsSelected();
    }
  }
  
}

customElements.define('combo-variant-radios', ComboVariantRadios);

class RemoveComboProduct extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.button = this.querySelector('a');
    if (this.button) {
      this.button.addEventListener('click', this.handleRemove.bind(this));
    }
  }

  async handleRemove(event) {
    event.preventDefault();

    const keys = (this.button.dataset.keys || '')
      .split(',')
      .map(k => k.trim())
      .filter(Boolean);

    if (!keys.length) {
      console.error('Geçersiz combo silme verisi.');
      return;
    }

    const updates = {};
    keys.forEach(key => {
      updates[key] = 0; // hepsini sil
    });

    try {
      await liquidAjaxCart.update({ updates });
    } catch (err) {
      console.error('❌ Combo silme isteği başarısız', err);
      showToast({
        containerClass: '.cart-drawer-combo-group',
        message: 'Kombin sepetten kaldırılırken bir hata oluştu.',
        insertAtIndex: 1,
      });
    }
  }
}

customElements.define('remove-combo-product', RemoveComboProduct);


class ComboQuantityControl extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.querySelector('.combo-quantity-plus')
      ?.addEventListener('click', () => this.updateQuantities(1));
    this.querySelector('.combo-quantity-minus')
      ?.addEventListener('click', () => this.updateQuantities(-1));
  }

  async updateQuantities(delta) {
    const btn = delta > 0
      ? this.querySelector('.combo-quantity-plus')
      : this.querySelector('.combo-quantity-minus');

    if (!btn) return;

    const keys = (btn.dataset.keys || '')
      .split(',')
      .map(k => k.trim())
      .filter(Boolean);

    const quantities = (btn.dataset.quantities || '')
      .split(',')
      .map(q => parseInt(q.trim(), 10));

    const inventories = (btn.dataset.inventories || '')
      .split(',')
      .map(i => parseInt(i.trim(), 10) || 9999);

    if (!keys.length || keys.length !== quantities.length) return;

    const updates = {};

    for (let i = 0; i < keys.length; i++) {
      const currentQty = quantities[i] || 0;
      const maxStock = inventories[i] || 9999;
      let nextQty = currentQty + delta;

      // 1'den aşağı inerken satırı sil
      if (delta < 0 && currentQty <= 1) {
        nextQty = 0;
      }

      if (nextQty > maxStock) {
        showToast({
          containerClass: '.cart-drawer-combo-group',
          message:
            'Bu kombindeki bazı ürünler stok sınırını aşıyor. Ürün adedi arttırılamaz.',
          insertAtIndex: 1,
        });
        return;
      }

      updates[keys[i]] = nextQty;
    }

    try {
      // 🔥 Tek seferde toplu update
      await liquidAjaxCart.update({ updates });
    } catch (err) {
      console.error('Combo quantity update failed', err);
      showToast({
        containerClass: '.cart-drawer-combo-group',
        message: 'Sepet güncellenirken bir hata oluştu.',
        insertAtIndex: 1,
      });
    }
  }
}

customElements.define('combo-quantity-control', ComboQuantityControl);


/* -------------------------------------------------------------------------- */
/*                                 DETAILS MODAL                              */
/* -------------------------------------------------------------------------- */

class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');

    this.detailsContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    this.querySelector('button[type="button"]').addEventListener('click', this.close.bind(this));

    this.summaryToggle.setAttribute('role', 'button');
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
  }
}

customElements.define('details-modal', DetailsModal);



/* -------------------------------------------------------------------------- */
/*                                SEARCH FORM                                 */
/* -------------------------------------------------------------------------- */
class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);

/* -------------------------------------------------------------------------- */
/*                             PREDICTIVE SEARCH                              */
/* -------------------------------------------------------------------------- */

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.beforeSearchElement = this.querySelector('.before-search');
    this.recentSearchKey = 'reflect_studio_recent_searches';
    this.recentSearchWrapper = this.querySelector('.recent-searches-wrapper');
    this.recentSearchList = this.querySelector('.recent-searches-list.searcable-keywords-list');
    this.renderRecentSearches();
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    this.closest('details')?.addEventListener('toggle', (e) => {
      const details = e.currentTarget;

      if (details.open && this.beforeSearchElement) {
        requestAnimationFrame(() => {
          const header = document.querySelector('.header');
          const announcementBar = document.querySelector('.announcement-bar');

          const headerHeight = header?.getBoundingClientRect().height || 0;
          const announcementHeight = announcementBar?.getBoundingClientRect().height || 0;

          const availableHeight = window.innerHeight - headerHeight - announcementHeight;

          this.beforeSearchElement.style.maxHeight = `${availableHeight}px`;
          this.beforeSearchElement.style.overflow = 'auto';
        });
      }
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (this.beforeSearchElement) {
      if (newSearchTerm.length > 0) {
        this.beforeSearchElement.style.display = 'none';
      } else {
        this.beforeSearchElement.style.display = '';
        requestAnimationFrame(() => {
          const header = document.querySelector('.header');
          const announcementBar = document.querySelector('.announcement-bar');

          const headerHeight = header?.getBoundingClientRect().height || 0;
          const announcementHeight = announcementBar?.getBoundingClientRect().height || 0;

          const availableHeight = window.innerHeight - headerHeight - announcementHeight;

          this.beforeSearchElement.style.maxHeight = `${availableHeight}px`;
        });
      }
    }

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const query = this.getQuery();

    if (!query.length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }

    this.saveRecentSearch(query);
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${window.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    if (this.beforeSearchElement && this.input.value.length === 0) {
      this.beforeSearchElement.style.display = '';
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    if (this.input.value.length === 0) {
      this.renderRecentSearches();
    }
  }

  saveRecentSearch(term) {
    const maxItems = 5;
    let recent = JSON.parse(localStorage.getItem(this.recentSearchKey)) || [];

    term = term.trim();
    if (!term || recent.includes(term)) return;

    recent.unshift(term);
    if (recent.length > maxItems) recent = recent.slice(0, maxItems);
    localStorage.setItem(this.recentSearchKey, JSON.stringify(recent));
    this.renderRecentSearches();
  }

  renderRecentSearches() {
    const recent = JSON.parse(localStorage.getItem(this.recentSearchKey)) || [];

    if (!recent.length) {
      this.recentSearchWrapper.style.display = 'none';
      return;
    }

    this.recentSearchWrapper.style.display = '';
    this.recentSearchList.innerHTML = '';

    recent.forEach((item) => {
      const el = document.createElement('div');
      el.className = 'recent-searches-item';
      el.textContent = item;
      el.addEventListener('click', () => {
        this.input.value = item;
        this.input.focus();
        this.input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.onChange(); // tetikle
      });

      const removeBtn = document.createElement('span');
      removeBtn.className = 'remove';
      removeBtn.textContent = '×';
      removeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.removeRecentSearch(item);
      });

      el.appendChild(removeBtn);
      this.recentSearchList.appendChild(el);
    });
  }

  removeRecentSearch(term) {
    let recent = JSON.parse(localStorage.getItem(this.recentSearchKey)) || [];
    recent = recent.filter((t) => t !== term);
    localStorage.setItem(this.recentSearchKey, JSON.stringify(recent));
    this.renderRecentSearches();
  }
}

customElements.define('predictive-search', PredictiveSearch);

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

/* -------------------------------------------------------------------------- */
/*                             DEFERRED MEDIA                                 */
/* -------------------------------------------------------------------------- */
class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (this.hasAttribute('data-autoload')) {
      const loadIfVisible = () => {
        // display:none değilse
        if (this.offsetParent !== null) this.loadContent(false);
      };
      loadIfVisible();
      window.addEventListener('resize', loadIfVisible, { passive: true });
    } else if (poster) {
      poster.addEventListener('click', this.loadContent.bind(this));
    }
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe, div'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName === 'VIDEO') {
        deferredElement.addEventListener('canplaythrough', () => {
          this.classList.add('is-ready');
        });
        if (deferredElement.getAttribute('autoplay')) {
          deferredElement.play();
        }
      } else if (deferredElement.nodeName === 'IFRAME') {
        setTimeout(() => {
          this.classList.add('is-ready');
        }, 2000);
      }

      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);


function showToast({ containerClass, message, duration = 3000, insertAtIndex = 0 }) {
  const container = document.querySelector(containerClass);

  if (!container) {
    console.warn("Belirtilen class bulunamadı:", containerClass);
    return;
  }

  const toast = document.createElement("div");
  toast.classList.add("toast-message");
  toast.textContent = message;

  const children = container.children;
  const index = Math.max(0, Math.min(insertAtIndex, children.length));

  if (children.length === 0 || index >= children.length) {
    container.appendChild(toast);
  } else {
    container.insertBefore(toast, children[index]);
  }

  // Fade-in animasyonu
  requestAnimationFrame(() => toast.classList.add("show"));

  // Fade-out + remove
  setTimeout(() => {
    toast.classList.remove("show");
    toast.classList.add("hide");
    toast.addEventListener("transitionend", () => toast.remove());
  }, duration);
}

window.addEventListener('load', () => 
  {
    document.querySelectorAll(".header__dropdown-content").forEach((e) => {
        if(e.childElementCount == 0){
            e.closest(".header__dropdown-inner").classList.add("no-header-content")
        }
    });
  }
);

document.addEventListener("DOMContentLoaded", function () {
  const wrappers = document.querySelectorAll(".banner-wrapper");

  wrappers.forEach(function (wrapper) {
    const img = wrapper.querySelector(".banner-image");
    const loader = wrapper.querySelector(".banner-loader");
    const picture = wrapper.querySelector(".banner-picture");

    if (img && loader && picture) {
      const showImage = () => {
        loader.style.display = "none";
        picture.classList.add("loaded");
      };

      if (img.complete) {
        showImage();
      } else {
        img.onload = showImage;
      }
    }
  });
});
